"use client"

import clsx from "clsx"
import { npSectionMtClassName } from "src/components/np-design/components/constants"
import { NpCarouselArrow } from "src/components/np-design/components/NpCarouselArrow"
import { NpPaginationDots } from "src/components/np-design/components/NpPaginationDots"
import { Img } from "src/components/unstyled/Img"
import { useEmbla } from "src/utilities/embla"
import { getPlaceholderUrl, ImageCdnOptions, toImageCdnUrl } from "src/utilities/images"
import { breakpoints } from "src/utilities/responsive"

const imageSizes = [1, 1.5, 2].map<ImageCdnOptions>((multiplier) => ({
  w: multiplier * breakpoints.xs,
  h: multiplier * breakpoints.xs,
  fit: "cover",
}))

const imageSizeMin = imageSizes.at(0)!
const imageSizeMax = imageSizes.at(-1)!

export type NpImageCarouselSectionProps = {
  images: string[]
}

export const NpImageCarouselSection = ({ images }: NpImageCarouselSectionProps) => {
  const { ref, embla, scrollSnaps, activeIndex, hasPrev, hasNext, justifyCenter } = useEmbla()

  return (
    <section className={clsx(npSectionMtClassName, "embla relative w-full")}>
      <div
        ref={ref}
        className={clsx("embla__viewport overflow-hidden", {
          "cursor-grab active:cursor-grabbing": hasPrev || hasNext,
        })}
      >
        <div
          className={clsx("embla__container -ml-2 flex max-h-full", {
            "justify-center": justifyCenter,
          })}
        >
          {images.map((image, index) => {
            const rawSrc = image || getPlaceholderUrl(imageSizeMax)
            const src = toImageCdnUrl(rawSrc, imageSizeMin)
            const srcSet = imageSizes.map((size) => `${toImageCdnUrl(rawSrc, size)} ${size.w}w`).join(", ")
            const sizes = `(max-width: ${breakpoints.md}px) 100vw, 640px`

            return (
              <div
                key={index}
                className="embla__slide max-h-full min-w-0 shrink-0 grow-0 basis-full pl-2 sm:max-h-160 sm:basis-162"
              >
                <Img
                  src={src}
                  srcSet={srcSet}
                  sizes={sizes}
                  alt=""
                  className="embla__slide_content size-full object-cover"
                />
              </div>
            )
          })}
        </div>
      </div>

      {hasPrev && (
        <div className="absolute top-1/2 left-4 hidden -translate-y-1/2 sm:block">
          <NpCarouselArrow side="left" onClick={() => embla?.scrollPrev()} />
        </div>
      )}
      {hasNext && (
        <div className="absolute top-1/2 right-4 hidden -translate-y-1/2 sm:block">
          <NpCarouselArrow side="right" onClick={() => embla?.scrollNext()} />
        </div>
      )}
      {!justifyCenter && (
        <div className="absolute right-0 bottom-4 left-0 flex justify-center sm:hidden">
          <NpPaginationDots count={scrollSnaps.length} activeIndex={activeIndex} overlayed />
        </div>
      )}
    </section>
  )
}
